import * as v from 'valibot'

const Id = v.pipe(v.string(), v.minLength(1))
const Count = v.pipe(v.number(), v.integer(), v.minValue(0))
const PositiveCount = v.pipe(v.number(), v.integer(), v.minValue(1))
const PositiveIntegerText = v.pipe(v.string(), v.regex(/^[1-9]\d*$/), v.transform(Number))

/** 作品状态（形态 6.6）：draft → published → unpublished → published；任意状态 → archived。 */
export const NOVEL_ADMIN_PACK_STATUSES = Object.freeze(['draft', 'published', 'unpublished', 'archived'])
export const NovelAdminPackStatus = v.picklist(NOVEL_ADMIN_PACK_STATUSES)

/**
 * GET /api/novel/admin/packs 的查询串：page 从 1 起，page_size 1 到 100（默认 20），status 缺省为全部状态；
 * q 按标题、slug、创作者邮箱包含匹配（服务端过滤，C16）。
 * optional 的默认值写成输入形态（字符串）：它和传入值一样要过后面的转换。
 * @typedef {{ page: number, page_size: number, status?: string, q?: string }} NovelAdminPackListQueryT
 */
export const NovelAdminPackListQuery = v.strictObject({
  page: v.optional(PositiveIntegerText, '1'),
  page_size: v.optional(v.pipe(PositiveIntegerText, v.maxValue(100)), '20'),
  status: v.optional(NovelAdminPackStatus),
  q: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(200))),
})

/**
 * 后台列表里的一部作品。validation_status 是当前内容的严格校验结论（上架要求 valid）；listed_at 为最近一次上架时间；
 * latest_version 是 current_version_id 指向的版本号（作者在创作台最近切出的版本，发布到正式环境用它）；featured_rank 为空表示不在精选里；
 * owner_email 取创作者账号的邮箱；时间都是 ISO 8601。
 * @typedef {{ id: string, slug: string, title: string, language: string|null, status: string, featured_rank: number|null, owner_email: string|null, validation_status: 'valid'|'invalid', listed_at: string|null, latest_version: string|null, current_version_id: string|null, updated_at: string }} NovelAdminPackRowT
 */
export const NovelAdminPackRow = v.object({
  id: Id,
  slug: Id,
  title: v.string(),
  language: v.nullable(v.string()),
  status: NovelAdminPackStatus,
  featured_rank: v.nullable(Count),
  owner_email: v.nullable(v.string()),
  validation_status: v.picklist(['valid', 'invalid']),
  listed_at: v.nullable(v.string()),
  latest_version: v.nullable(v.string()),
  current_version_id: v.nullable(Id),
  published_version_id: v.nullable(Id),
  updated_at: v.string(),
})

/** @typedef {{ items: NovelAdminPackRowT[], total: number, page: number, pages: number, page_size: number }} NovelAdminPackListT */
export const NovelAdminPackList = v.object({
  items: v.array(NovelAdminPackRow),
  total: Count,
  page: PositiveCount,
  pages: Count,
  page_size: PositiveCount,
})

/**
 * 详情里的作品：列表行加上创作者 id、封面地址、当前内容哈希与创建时间。
 * 正式环境里由发布通道建的作品没有创作者，owner_user_id 为 null（C25）。
 */
export const NovelAdminPackInfo = v.object({
  ...NovelAdminPackRow.entries,
  owner_user_id: v.nullable(Id),
  cover_url: v.nullable(v.string()),
  content_hash: v.string(),
  created_at: v.string(),
})

export const NovelAdminPackVersion = v.object({
  id: Id,
  version: v.string(),
  // 作者切版本时写的说明（C24），没写为 null。
  note: v.nullable(v.string()),
  package_hash: v.string(),
  play_config_hash: v.nullable(v.string()),
  source_revision: v.nullable(v.number()),
  config_status: v.picklist(['frozen', 'legacy_unrecorded']),
  published_at: v.string(),
  // 版本内容按当前规则的严格校验结论；正式环境只收 valid 的版本（C25）。
  validation_status: v.picklist(['valid', 'invalid']),
})

export const NovelAdminPackAsset = v.object({
  id: Id,
  kind: v.picklist(['cover', 'location', 'state_image', 'item']),
  public_url: v.string(),
  width: v.nullable(Count),
  height: v.nullable(Count),
  created_at: v.string(),
})

/**
 * GET /api/novel/admin/packs/:id：作品、当前完整内容（content_json）与简介（与书架卡片同一条规则）供后台预览（C13）、
 * 版本列表（只读，新的在前，带说明与校验状态）、素材列表。
 */
export const NovelAdminPackDetail = v.object({
  pack: NovelAdminPackInfo,
  content: v.record(v.string(), v.unknown()),
  teaser: v.pipe(v.string(), v.maxLength(120)),
  versions: v.array(NovelAdminPackVersion),
  assets: v.array(NovelAdminPackAsset),
})

/** 上架、下架、精选的响应：动作之后的作品行。 */
export const NovelAdminPackResponse = v.object({ pack: NovelAdminPackRow })

/** PUT /api/novel/admin/packs/:id/featured：非负整数，null 表示取消精选；缺 featured_rank 键一律 400。 */
export const NovelFeaturedBody = v.strictObject({ featured_rank: v.nullable(Count) })

